/**
 * App shell.
 *
 * Nav is grouped by what the merchant is trying to do, not by which tier we
 * shipped the feature in. "Run" is the daily loop, "Intelligence" is what they
 * can't get anywhere else, "Setup" is configured once and forgotten.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { api, auth, isEmbedded } from '../api';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  badge?: string;
}

const NAV_GROUPS: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Run',
    items: [
      { to: '/app', label: 'Dashboard', end: true },
      { to: '/app/copilot', label: 'ScaleAds Copilot', badge: 'AI' },
      { to: '/app/campaigns', label: 'Campaigns' },
      { to: '/app/creatives', label: 'Creative Studio' },
      { to: '/app/recommendations', label: 'Recommendations' },
      { to: '/app/operator', label: 'AI Operator' },
    ],
  },
  {
    heading: 'Intelligence',
    items: [
      { to: '/app/competitors', label: 'Competitor Intel' },
      { to: '/app/benchmarks', label: 'Benchmarks & true ROAS' },
      { to: '/app/reddit', label: 'Reddit Insights' },
      { to: '/app/funnels', label: 'Funnels' },
    ],
  },
  {
    heading: 'Setup',
    items: [
      { to: '/app/products', label: 'Products' },
      { to: '/app/channels', label: 'Ad channels' },
      { to: '/app/pixel', label: 'Pixel setup' },
      { to: '/app/billing', label: 'Billing' },
    ],
  },
];

export default function Layout() {
  const [pending, setPending] = useState(0);
  const [connected, setConnected] = useState<string[]>([]);

  useEffect(() => {
    api
      .recommendations('pending')
      .then((r) => setPending(r.length))
      .catch(() => undefined);
    api
      .connections()
      .then((c) => setConnected(c.filter((x) => x.status === 'active').map((x) => x.platform)))
      .catch(() => undefined);
  }, []);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          ScaleAds AI
          <small>OS 3.0</small>
        </div>

        <nav>
          {NAV_GROUPS.map((g) => (
            <div key={g.heading} className="nav-group">
              <div className="nav-heading">{g.heading}</div>
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <span>{n.label}</span>
                  {n.badge && <em className="nav-badge">{n.badge}</em>}
                  {n.to === '/app/recommendations' && pending > 0 && (
                    <em className="nav-count">{pending}</em>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="store">
          <b>Standalone workspace</b>
          Direct account
          <div className="store-badges">
            {connected.length === 0 ? (
              <NavLink to="/app/channels" className="badge">
                connect a channel
              </NavLink>
            ) : (
              connected.map((p) => (
                <span key={p} className={`badge b-${p}`}>
                  {p}
                </span>
              ))
            )}
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <h1>ScaleAds AI</h1>
          <div className="spacer" />
          <NavLink to="/app/copilot" className="btn">
            Ask Copilot
          </NavLink>
          <NavLink to="/app/campaigns/new" className="btn btn-primary">
            + Launch campaign
          </NavLink>
          {!isEmbedded() && (
            <button
              className="btn"
              onClick={() => {
                // Best-effort server-side revocation, then clear locally either
                // way — a network failure must not trap someone signed in.
                void api.logout().catch(() => undefined);
                auth.clear();
                window.location.href = '/';
              }}
            >
              Sign out
            </button>
          )}
        </div>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
